/**
 * WhatsApp template registry (R17: business initiated messages use approved templates only).
 * Copy never contains the product codename (R3); the brand arrives as the `app` variable.
 */
import { FOOTERS, LOCALES } from './whatsapp-locales.js';
const RAW = {
    team_invite: {
        variables: ['inviter', 'agency', 'app', 'link'],
        text: {
            en: '{{agency}}: {{inviter}} invited you to join their team on {{app}}. Open this link to accept: {{link}}',
        },
    },
    visit_request_received: {
        variables: ['agency', 'property', 'slot', 'link', 'app'],
        text: {
            en: '{{agency}}: we received your visit request for {{property}} ({{slot}}). Tap to confirm it is you: {{link}}',
        },
    },
    renewal_reminder_tenant: {
        variables: ['agency', 'property', 'date', 'app'],
        text: {
            en: '{{agency}}: your rental agreement for {{property}} ends on {{date}}. Please talk to us about renewing it.',
        },
    },
    renewal_reminder_owner: {
        variables: ['agency', 'property', 'date', 'app'],
        text: {
            en: '{{agency}}: the rental agreement for {{property}} ends on {{date}}. Talk to us about renewing it with your tenant.',
        },
    },
    rent_reminder: {
        variables: ['agency', 'property', 'amount', 'day'],
        text: {
            en: "{{agency}}: a reminder that this month's rent of {{amount}} for {{property}} is due on day {{day}} of each month. Your landlord asked us to send it and can stop it at any time.",
        },
    },
    // ---- Tenant: the state of the visit ----
    visit_confirmed: {
        variables: ['agency', 'property', 'slot', 'address', 'map', 'phone', 'app'],
        text: {
            en: '{{agency}}: your visit to {{property}} is confirmed ({{slot}}). Address: {{address}}. Map: {{map}} To reach us, call {{phone}}.',
        },
    },
    visit_rescheduled: {
        variables: ['agency', 'property', 'slot', 'address', 'map', 'phone', 'app'],
        text: {
            en: '{{agency}}: your visit to {{property}} has moved ({{slot}}). Address: {{address}}. Map: {{map}} To reach us, call {{phone}}.',
        },
    },
    visit_cancelled: {
        variables: ['agency', 'property', 'phone', 'app'],
        text: {
            en: '{{agency}}: your visit to {{property}} was cancelled. To book another time, call {{phone}}.',
        },
    },
    visit_done: {
        variables: ['agency', 'property', 'phone'],
        text: {
            en: '{{agency}}: your visit to {{property}} is recorded as done. If you decide to take it, the next step is a token. To go ahead, call {{phone}}.',
        },
    },
    visit_reminder_evening: {
        variables: ['agency', 'property', 'slot', 'address', 'map', 'phone', 'app'],
        text: {
            en: '{{agency}}: a reminder that your visit to {{property}} is tomorrow ({{slot}}). Address: {{address}}. Map: {{map}} To reach us, call {{phone}}.',
        },
    },
    visit_reminder_2h: {
        variables: ['agency', 'property', 'slot', 'address', 'map', 'phone', 'app'],
        text: {
            en: '{{agency}}: your visit to {{property}} is in about two hours ({{slot}}). Address: {{address}}. Map: {{map}} To reach us, call {{phone}}.',
        },
    },
    // ---- Owner: what happens to their flat. The first one they get says who added their number. ----
    owner_intro: {
        variables: ['agency', 'broker', 'property', 'app'],
        text: {
            en: '{{agency}}: {{broker}} added your number for {{property}}, to keep you updated about visits and the deal. Tell {{broker}} if this is a mistake.',
        },
    },
    owner_visit_request: {
        variables: ['agency', 'property', 'slot', 'broker', 'app'],
        text: {
            en: '{{agency}}: a client asked to visit {{property}} ({{slot}}). {{broker}} is arranging it.',
        },
    },
    owner_visit_done: {
        variables: ['agency', 'property', 'broker', 'app'],
        text: {
            en: '{{agency}}: a visit to {{property}} is done. {{broker}} will be in touch about the next step.',
        },
    },
    owner_token_recorded: {
        variables: ['agency', 'property', 'amount', 'app'],
        text: {
            en: '{{agency}}: a token of {{amount}} was recorded for {{property}}. The brokerage memo comes next.',
        },
    },
    // The owner hears what the tenant hears: the visit confirmed, moved and cancelled (the client's
    // name and the time), the deal registered and complete, and what the owner paid in brokerage.
    owner_visit_confirmed: {
        variables: ['agency', 'property', 'client', 'slot', 'broker', 'app'],
        text: {
            en: '{{agency}}: the visit of {{client}} to {{property}} is confirmed ({{slot}}). {{broker}} is arranging it.',
        },
    },
    owner_visit_moved: {
        variables: ['agency', 'property', 'client', 'slot', 'broker', 'app'],
        text: {
            en: '{{agency}}: the visit of {{client}} to {{property}} has moved ({{slot}}). {{broker}} is arranging it.',
        },
    },
    owner_visit_cancelled: {
        variables: ['agency', 'property', 'client', 'slot', 'broker', 'app'],
        text: {
            en: '{{agency}}: the visit of {{client}} to {{property}} ({{slot}}) was cancelled. Talk to {{broker}} if you have questions.',
        },
    },
    owner_deal_registered: {
        variables: ['agency', 'property', 'date', 'link', 'app'],
        text: {
            en: '{{agency}}: the rental agreement for {{property}} was registered on {{date}}. The agreement, the registration copy and the key dates are here: {{link}}',
        },
    },
    owner_deal_closed: {
        variables: ['agency', 'property', 'app'],
        text: {
            en: '{{agency}}: the deal for {{property}} is complete. Thank you for choosing {{agency}}.',
        },
    },
    owner_brokerage_receipt: {
        variables: ['agency', 'property', 'amount', 'date', 'app'],
        text: {
            en: '{{agency}}: we received your brokerage of {{amount}} for {{property}} on {{date}}. Thank you.',
        },
    },
    // ---- Tenant: the deal reaches its end ----
    deal_registered: {
        variables: ['agency', 'property', 'date', 'link', 'app'],
        text: {
            en: '{{agency}}: the rental agreement for {{property}} was registered on {{date}}. Your agreement, the registration copy and the key dates are here: {{link}}',
        },
    },
    deal_closed: {
        variables: ['agency', 'property', 'app'],
        text: {
            en: '{{agency}}: your deal for {{property}} is complete. Thank you for choosing {{agency}}.',
        },
    },
    // ---- The brokerage memo, for each of the three who sign ----
    memo_link: {
        variables: ['agency', 'property', 'link', 'app'],
        text: {
            en: '{{agency}}: the brokerage memo for {{property}} is ready to read and sign. Open it here: {{link}}',
        },
    },
    memo_link_updated: {
        variables: ['agency', 'property', 'version', 'link', 'app'],
        text: {
            en: '{{agency}}: the brokerage memo for {{property}} was changed (version {{version}}). Please read and sign the new one: {{link}}',
        },
    },
    memo_signed_copy: {
        variables: ['agency', 'property', 'link', 'app'],
        text: {
            en: '{{agency}}: everyone has signed the brokerage memo for {{property}}. Your signed copy is here: {{link}}',
        },
    },
    // ---- A reminder of the brokerage a person owes, sent when the broker taps it ----
    brokerage_due: {
        variables: ['agency', 'property', 'amount', 'due', 'phone', 'app'],
        text: {
            en: '{{agency}}: a reminder that your share of the brokerage for {{property}} is {{amount}}{{due}}. Questions? Call {{phone}}.',
        },
    },
    owner_brokerage_due: {
        variables: ['agency', 'property', 'amount', 'due', 'phone', 'app'],
        text: {
            en: '{{agency}}: a reminder that your share of the brokerage for {{property}} is {{amount}}{{due}}. Questions? Call {{phone}}.',
        },
    },
    // ---- The same three for the owner, who also gets the way to stop ----
    owner_memo_link: {
        variables: ['agency', 'property', 'link', 'app'],
        text: {
            en: '{{agency}}: the brokerage memo for {{property}} is ready to read and sign. Open it here: {{link}}',
        },
    },
    owner_memo_link_updated: {
        variables: ['agency', 'property', 'version', 'link', 'app'],
        text: {
            en: '{{agency}}: the brokerage memo for {{property}} was changed (version {{version}}). Please read and sign the new one: {{link}}',
        },
    },
    owner_memo_signed_copy: {
        variables: ['agency', 'property', 'link', 'app'],
        text: {
            en: '{{agency}}: everyone has signed the brokerage memo for {{property}}. Your signed copy is here: {{link}}',
        },
    },
};
/**
 * The Meta category of every template. Every message here follows something the person did or
 * asked for (a visit, a deal, a memo), so all are utility. A template that promotes anything must
 * be marked marketing here, and it is then off by default (docs/WHATSAPP_TEMPLATES.md).
 */
export const TEMPLATE_CATEGORY = {
    team_invite: 'utility',
    visit_request_received: 'utility',
    renewal_reminder_tenant: 'utility',
    renewal_reminder_owner: 'utility',
    rent_reminder: 'utility',
    visit_confirmed: 'utility',
    visit_rescheduled: 'utility',
    visit_cancelled: 'utility',
    visit_done: 'utility',
    visit_reminder_evening: 'utility',
    visit_reminder_2h: 'utility',
    owner_intro: 'utility',
    owner_visit_request: 'utility',
    owner_visit_done: 'utility',
    owner_token_recorded: 'utility',
    owner_visit_confirmed: 'utility',
    owner_visit_moved: 'utility',
    owner_visit_cancelled: 'utility',
    owner_deal_registered: 'utility',
    owner_deal_closed: 'utility',
    owner_brokerage_receipt: 'utility',
    deal_registered: 'utility',
    deal_closed: 'utility',
    memo_link: 'utility',
    memo_link_updated: 'utility',
    memo_signed_copy: 'utility',
    brokerage_due: 'utility',
    owner_brokerage_due: 'utility',
    owner_memo_link: 'utility',
    owner_memo_link_updated: 'utility',
    owner_memo_signed_copy: 'utility',
};
export const TEMPLATE_APPROVAL = Object.fromEntries(Object.keys(TEMPLATE_CATEGORY).map((name) => [name, 'draft']));
/** The languages a template has its own text in; English is always one. */
export function templateLanguages(name) {
    return LOCALES[name] === undefined ? ['en'] : ['en', 'mr', 'hi'];
}
/** Templates sent to an owner: they carry the "Wrong number or stop" link. */
export const OWNER_TEMPLATES = new Set([
    'renewal_reminder_owner',
    'owner_intro',
    'owner_visit_request',
    'owner_visit_done',
    'owner_token_recorded',
    'owner_visit_confirmed',
    'owner_visit_moved',
    'owner_visit_cancelled',
    'owner_deal_registered',
    'owner_deal_closed',
    'owner_brokerage_receipt',
    'owner_brokerage_due',
    'owner_memo_link',
    'owner_memo_link_updated',
    'owner_memo_signed_copy',
]);
/** A team invite is the one message that does not carry the footer. */
const NO_FOOTER = new Set(['team_invite']);
/** Adds the languages and the footer to the raw definitions, and the variables the footer needs. */
function build() {
    const out = {};
    for (const name of Object.keys(RAW)) {
        const raw = RAW[name];
        const owner = OWNER_TEMPLATES.has(name);
        const footer = !NO_FOOTER.has(name);
        const variables = [
            ...raw.variables.filter((v) => v !== 'app'),
            ...(footer ? ['about'] : []),
            ...(owner ? ['stop'] : []),
            'app',
        ];
        const text = {};
        for (const lang of ['en', 'mr', 'hi']) {
            const core = lang === 'en' ? raw.text.en : (LOCALES[name]?.[lang] ?? raw.text.en);
            text[lang] = footer
                ? `${core} ${FOOTERS[lang].all}${owner ? ` ${FOOTERS[lang].stop}` : ''}`
                : core;
        }
        out[name] = { variables, text };
    }
    return out;
}
const TEMPLATES = build();
export const WHATSAPP_TEMPLATE_NAMES = Object.keys(TEMPLATES);
export function isApprovedTemplate(name) {
    return Object.hasOwn(TEMPLATES, name);
}
/** Renders a template. Throws for an unknown template or a missing variable. */
export function renderWhatsAppTemplate(name, variables, locale = 'en') {
    if (!isApprovedTemplate(name))
        throw new Error(`"${name}" is not an approved template`);
    const template = TEMPLATES[name];
    for (const key of template.variables) {
        if (variables[key] === undefined) {
            throw new Error(`Template "${name}" needs the variable "${key}"`);
        }
    }
    const text = template.text[locale] ?? template.text.en;
    return text.replace(/\{\{(\w+)\}\}/g, (_match, key) => variables[key] ?? '');
}
/** The placeholders of a template in the order a provider needs them (positional parameters). */
export function whatsAppTemplateVariables(name) {
    if (!isApprovedTemplate(name))
        throw new Error(`"${name}" is not an approved template`);
    const template = TEMPLATES[name];
    return template.variables;
}
//# sourceMappingURL=whatsapp.js.map